import React from 'react';
import type { Styles } from 'react-select';
import styled from 'styled-components';
import {
  CaretIcon,
  NoResultsIcon,
  SelectLogoEmptyStateIcon,
} from '@rsos/base-assets/icons';
import Box from '@rsos/base-components/Box';
import Text from '@rsos/base-components/Text';

// react-select v4 uses CSSObject from @emotion/serialize but doesn't re-export it.
// Extract it from the exported Styles type to avoid depending on a transitive package.
type CSSObject = Parameters<
  NonNullable<Styles<never, false>['clearIndicator']>
>[0];

interface SelectThemeColors {
  background: string;
  foreground: string;
  heavyLineOutline: string;
  highlights: string;
  lightDanger: string;
  lightLineDivider: string;
  primaryBase: string;
  primaryText: string;
  secondaryLink: string;
}

interface SelectTheme {
  colors: SelectThemeColors;
}

interface SelectStyleProps {
  hasError?: boolean;
  noGap?: boolean;
  theme: SelectTheme;
}

interface SelectStyleState {
  isFocused?: boolean;
  isSelected?: boolean;
  menuIsOpen?: boolean;
  selectProps: SelectStyleProps;
}

export const customGenericStyles = {
  clearIndicator: (styles: CSSObject, state: SelectStyleState) => ({
    ...styles,
    color: state.selectProps.theme.colors.secondaryLink,
    padding: 0,
  }),
  input: (styles: CSSObject) => ({
    ...styles,
  }),
  singleValue: (styles: CSSObject) => ({
    ...styles,
    color: 'inherit',
    fontSize: '14px',
  }),
  dropdownIndicator: (styles: CSSObject) => ({
    ...styles,
  }),
  control: (styles: CSSObject, state: SelectStyleState) => ({
    ...styles,
    minHeight: 35,
    borderColor: `${
      state.menuIsOpen || state.isFocused
        ? state.selectProps.hasError
          ? state.selectProps.theme.colors.lightDanger
          : state.selectProps.theme.colors.primaryBase
        : state.selectProps.theme.colors.heavyLineOutline
    } !important`,
    borderRadius:
      state.selectProps.noGap && state.menuIsOpen ? '5px 5px 0 0' : 5,
    boxShadow: 'none',
    color: state.selectProps.theme.colors.primaryText,
  }),
  valueContainer: (styles: CSSObject) => ({
    ...styles,
  }),
  indicatorsContainer: (styles: CSSObject) => ({
    ...styles,
    padding: '2px 0px',
  }),
  noOptionsMessage: (styles: CSSObject, state: SelectStyleState) => ({
    ...styles,
    color: state.selectProps.theme.colors.primaryText,
  }),
  menu: (styles: CSSObject, state: SelectStyleState) => ({
    ...styles,
    marginTop: state.selectProps.noGap ? 0 : '10px',
    border: `1px solid ${state.selectProps.theme.colors.lightLineDivider}`,
    background: state.selectProps.theme.colors.background,
    boxShadow: '2px 2px 10px 1px rgba(0,0,0,0.13)',
    borderRadius: state.selectProps.noGap ? '0 0 5px 5px' : 5,
  }),
  menuList: (styles: CSSObject) => ({
    ...styles,
    paddingTop: 0,
    paddingBottom: 0,
    borderRadius: 5,
  }),
  option: (styles: CSSObject, state: SelectStyleState) => ({
    ...styles,
    color: state.selectProps.theme.colors.primaryText,
    fontSize: '14px',
    background: state.isSelected
      ? state.selectProps.theme.colors.highlights
      : state.selectProps.theme.colors.background,
    '&:hover': {
      background:
        !state.isSelected && state.selectProps.theme.colors.foreground,
    },
    '&:active': {
      background: state.isSelected && state.selectProps.theme.colors.highlights,
    },
  }),
};

export const customMultiSelectStyles = {
  multiValue: (styles: CSSObject, state: SelectStyleState) => ({
    ...styles,
    backgroundColor: state.selectProps.theme.colors.foreground,
  }),
  multiValueRemove: (styles: CSSObject, state: SelectStyleState) => ({
    ...styles,
    ':hover': {
      backgroundColor: state.selectProps.theme.colors.lightLineDivider,
      cursor: 'pointer',
    },
    ':active': {
      backgroundColor: state.selectProps.theme.colors.secondaryLink,
    },
  }),
  multiValueLabel: (styles: CSSObject, state: SelectStyleState) => ({
    ...styles,
    display: 'flex',
    alignItems: 'center',
    color: state.selectProps.theme.colors.primaryText,
    height: 25,
    borderRadius: 5,
    fontSize: '12px',
    padding: '5px',
  }),
};
const SelectPlaceholder = styled(Text).attrs({
  color: 'secondaryText',
  pl: '2px',
  size: 'normal',
})``;
type PlaceholderProps = React.PropsWithChildren<{ theme?: SelectTheme }>;

export const Placeholder = (props: PlaceholderProps) => (
  <Box position="absolute">
    <SelectPlaceholder theme={props.theme}>{props.children}</SelectPlaceholder>
  </Box>
);

// TODO: confirm the following: according to the design system, the caret height and width are 6px and 11px respectively https://app.sympli.io/app#!/designs/60675e0df475fb470607a790/specs/assets. the CRG design has 5px and 9px https://app.sympli.io/app#!/designs/60da07fb9a78ce01d1d914a1/specs
export const ChevronContainer = styled(Box).attrs({
  align: 'center',
  justify: 'center',
  width: 30,
  height: 30,
  cursor: 'pointer',
  color: 'secondaryLink',
  transition: '300ms ease transform',
})``;

export const DropdownIndicator = (props: {
  selectProps: { menuIsOpen: boolean };
}) => (
  <ChevronContainer
    style={{
      transform: `rotate(${props.selectProps.menuIsOpen ? -180 : 0}deg)`,
    }}
    data-name="select-arrow"
  >
    <CaretIcon width={11} height={13} />
  </ChevronContainer>
);

export const NoOptionsMessage = (props: {
  options: unknown[];
  selectProps: {
    inputValue:
      | string
      | number
      | boolean
      | React.ReactElement<
          unknown,
          string | React.JSXElementConstructor<unknown>
        >
      | Iterable<React.ReactNode>
      | React.ReactPortal
      | null
      | undefined;
  };
}) => {
  return props.options.length ? (
    <Box justify="center" align="center" py={20} flow={9}>
      <Box horizontal flow={7} color="secondaryText">
        <NoResultsIcon />
        <Text fontWeight="bold">No Results</Text>
      </Box>
      <Text size="small" color="secondaryText">
        0 Results found for "{props.selectProps.inputValue}"
      </Text>
    </Box>
  ) : (
    <Box align="center" justify="center" p={30} flow={7} color="secondaryText">
      <SelectLogoEmptyStateIcon
        viewboxHeight={100}
        viewboxWidth={100}
        width={55}
        height={55}
      />
    </Box>
  );
};
